import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import { hashSha256 } from '@/utils/services/security';
import { desCrypto, setCrypto } from '@/utils/services/cookies';
import solicitud from '@/models/solicitud';

// Paso exclusivo del representante legal de una solicitud juridica (RF-ONB-19, AB#10741/US-7).
// Vive como constante en codigo y no como un flag dentro del Flujo.Body porque el editor de flujos
// del backoffice reconstruye cada paso desde el catalogo y descarta las propiedades que no conoce:
// un flag ahi no sobreviviria al primer guardado.
const STEP_SOLO_REPRESENTANTE_LEGAL = '/conformidad-rl';

const initialState = {
  steps: [],
  step: '',
  stepObj: {},
  stepActual: '',
  color: '',
  navbarVisible: false,
  showQR: false,
  type: '',
  entidadId: '',
  spin: false,
  screen: '',
  form: {},
  formProperty: '',
  frontImage: null,
  backImage: null,
  selfieImage: null,
  documentosJuridico: [],
  btnDisabled: false,
  useForm: {},
  camaraVisible: false,
  cameraType: null,
  isMobile: false,
  validateFeVida: false,
  progress: 0, // Nuevo
  logo: null,
};

export const useStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Steps related methods
        getSteps: () => desCrypto(get().steps),

        setSteps: async (steps) => {
          const getTypeStep = async (steps) => {
            const type = get().type;

            switch (type) {
              case 'juridico':
                return steps.jsonPersonaJuridica;
              case 'retoma':
                return steps;
              default:
                // El representante legal de una juridica y una persona fisica individual comparten
                // este mismo array de pasos: no hay un TipoFlujo propio para RL. Lo unico que los
                // distingue es que la invitacion del representante trae el id de la solicitud
                // juridica en el parametro 's', que page.tsx guarda en 'solicitudJ'.
                //
                // Sin este filtro, la vista previa de los datos de la empresa (RF-ONB-19) le
                // aparaceria tambien a quien se da de alta para si mismo, donde no hay empresa que
                // mostrar. Se filtra aca y no auto-avanzando desde la pagina porque el paso seguiria
                // ocupando un indice, y toda la navegacion es por indice: el boton Anterior de
                // /documento y el contador de progreso quedarian corridos.
                //
                // Es inerte mientras la entidad no configure el paso: filtra una url que hoy no
                // existe en ningun Flujo.Body.
                //
                // Truthiness y no != null: page.tsx guarda '' cuando el link no trae 's'.
                return !!sessionStorage.getItem('solicitudJ')
                  ? steps.jsonPersonaFisica
                  : steps.jsonPersonaFisica.filter((step) => step.url !== STEP_SOLO_REPRESENTANTE_LEGAL);
            }
          };

          const typeStep = await getTypeStep(steps);

          const hashedSteps = await Promise.all(
            typeStep.map(async (step) => ({
              ...step,
              hash: await hashSha256(process.env.NEXT_PUBLIC_HASH_KEY + step.url),
            }))
          );
          set({ steps: setCrypto(hashedSteps) });
          set({ stepObj: hashedSteps[0] });
        },

        validateStep: async (fullPath) => {
          const steps = desCrypto(get().steps);
          const currentUrl = fullPath;
          const currentHash = await hashSha256(process.env.NEXT_PUBLIC_HASH_KEY + currentUrl);
          const allowedHash = get().step;

          let allowedIndex = steps.findIndex((x) => x.hash === allowedHash);
          allowedIndex = allowedIndex === -1 ? 0 : allowedIndex;
          const currentIndex = steps.findIndex((x) => x.hash === currentHash);

          const allowed = currentIndex > -1 && currentIndex <= allowedIndex;

          if (allowed) {
            set({ screen: solicitud.screens.instructions });
          }

          // AB#11070: si steps quedo vacio (o el indice no resuelve) no hay a donde
          // redirigir dentro del wizard; se cae a la pantalla de bienvenida en vez de crashear.
          return allowed ? undefined : (steps[allowedIndex]?.url ?? '/');
        },

        updateStep: (router) => {
          const { pathname } = router;

          if (pathname === '/procesando') {
            set({ step: { url: pathname, title: 'En Proceso' } });
            return;
          }

          if (pathname === '/finalizar') {
            set({ step: { url: pathname, title: '¡Bienvenid@!' } });
            return;
          }

          const url = pathname;
          const hash = hashSha256(process.env.NEXT_PUBLIC_HASH_KEY + url);
          const title = url === '/' ? 'Inicio' : 'Paso';

          set({ step: { url, title, hash } });
        },

        moveStep: async (fullPath, router, direction) => {
          const steps = desCrypto(get().steps);
          const path = fullPath.split('/');
          const currentUrl = path[path.length - 1];
          const currentIndex = steps.findIndex((x) => x.url === '/' + currentUrl);

          let newIndex;
          if (direction === 'next') {
            newIndex = steps[currentIndex + 1];
          } else {
            newIndex = steps[currentIndex - 1];
          }

          set({ step: newIndex.hash });
          set({ stepObj: newIndex });
          router.push(newIndex.url);

          return true;
        },

        // Checklist related methods
        // setStepObj: (stepObj) => set({ stepObj }),
        setScreen: (screen) => set({ screen }),
        setForm: (form) => set(form),
        setFormProperty: (stepKey, formValues) =>
          set((state) => ({
            form: {
              ...state.form,
              [stepKey]: { ...state.form[stepKey], ...formValues },
            },
          })),

        updateForm: (values) => {
          set((state) => ({
            form: {
              ...state.form,
              ...values,
            },
          }));
        },

        changeScreen: (newScreen) => {
          set({ screen: newScreen });
        },

        setColor: (color) => set({ color }),
        setNavbarVisible: (navbarVisible) => set({ navbarVisible }),
        setShowQR: (showQR) => set({ showQR }),
        setType: (type) => set({ type }),
        setEntidadId: (entidadId) => set({ entidadId }),
        setSpin: (spin) => set({ spin }),
        setSelfieImage: (image) => set({ selfieImage: image }),
        setFrontImage: (image) => set({ frontImage: image }),
        setBackImage: (image) => set({ backImage: image }),
        resetStore: () => set(initialState),
        setBtnDisabled: (btnDisabled) => set({ btnDisabled }),
        setUseForm: (useForm) => set({ useForm }),
        setCamaraVisible: (camaraVisible) => set({ camaraVisible }),
        setCameraType: (type) => set(() => ({ cameraType: type })),
        setIsMobile: (isMobile) => set({ isMobile }),
        setValidateFeVida: (validateFeVida) => set({ validateFeVida: validateFeVida }),
        setProgress: (value) => set({ progress: value }),
        setLogo: (logo) => set({ logo: logo }),
      }),
      {
        name: 'storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
);

export const useImageStore = create(
  devtools((set) => ({
    ...initialState,
    setFrontImage: (image) => set({ frontImage: image }),
    setBackImage: (image) => set({ backImage: image }),
    setSelfieImage: (image) => set({ selfieImage: image }),
  }))
);

export const useDocumentStore = create(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setDocuments: (array) => set({ documentosJuridico: array }),
      }),
      {
        name: 'storageDocumentos',
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
);

export const useDocumentPropietariosStore = create(
  devtools(
    persist(
      (set) => ({
        documentoPropietarios: null,
        pathDocumentosBeneficiario: null,

        setDocuments: (array) => set({ documentoPropietarios: array }),

        // Cada beneficiario tiene un objeto con las dos caras del documento que adjunta:
        // { frente, dorso }, cada una { uid, archivoId }. Reemplaza a
        // setPathDocumentsBeneficarios/deleteGUID, que asumian un unico documento por
        // beneficiario. Hasta AB#10831 eran cuatro slots, uno por cara y tipo de documento.
        setDocumentBeneficiario: (index, slot, uid, archivoId) =>
          set((state) => {
            const updatedDocs = [...(state.pathDocumentosBeneficiario || [])];
            updatedDocs[index] = { ...(updatedDocs[index] || {}), [slot]: { uid, archivoId } };
            return { pathDocumentosBeneficiario: updatedDocs };
          }),
        deleteDocumentBeneficiario: (index, slot) =>
          set((state) => {
            const updatedDocs = [...(state.pathDocumentosBeneficiario || [])];
            const documentosBeneficiario = { ...(updatedDocs[index] || {}) };
            delete documentosBeneficiario[slot];
            updatedDocs[index] = documentosBeneficiario;
            return { pathDocumentosBeneficiario: updatedDocs };
          }),
        // Saca al beneficiario completo de la lista (cuando se elimina la fila del form).
        deletePathDocumentsBeneficarios: (index) =>
          set((state) => {
            const updatedDocs = [...(state.pathDocumentosBeneficiario || [])];
            updatedDocs.splice(index, 1);
            return { pathDocumentosBeneficiario: updatedDocs };
          }),
      }),
      {
        name: 'storagePropietarios',
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
);

export const useDocumentsDeclaracionesStore = create(
  devtools(
    persist(
      (set) => ({
        documentosDeclaracionesJuridico: null,
        documentosDeclaracionesRL: null,

        setDocumentsJuridico: (base64) => set({ documentosDeclaracionesJuridico: base64 }),
        setDocumentsRL: (base64) => set({ documentosDeclaracionesRL: base64 }),
      }),
      {
        name: 'storageDeclaraciones',
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
);
